import React, { useRef, useState, useEffect } from "react";
import { TabButton } from "../components/Buttons"; // Adjust import path
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import ProductCard from "../components/Home/ProductCard";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Fragrances = ({ title, products }) => {
  const [activeTab, setActiveTab] = useState("New Arrivals");
  const swiperRef = useRef(null);
  const slidesContainerRef = useRef(null);
  const titleRef = useRef(null);

  const tabs = [
    { id: 1, title: "New Arrivals" },
    { id: 2, title: "Best Sales" },
    { id: 3, title: "Featured" },
  ];

  // Filter products based on active tab safely
  const filteredProducts = products?.filter((product) =>
    product.tags?.length
      ? product.tags.some((tag) =>
          tag.toLowerCase().includes(activeTab.toLowerCase())
        )
      : true
  ) || [];

  const handleTabClick = (tabTitle) => {
    setActiveTab(tabTitle);
  };

  // Animate title
  useEffect(() => {
    if (!titleRef.current) return;
    gsap.from(titleRef.current, {
      y: -50,
      opacity: 0,
      duration: 1,
      ease: "power3.out",
      scrollTrigger: {
        trigger: titleRef.current,
        start: "top 90%",
      },
    });
  }, []);

  // Animate slides after products load
  useEffect(() => {
    if (!slidesContainerRef.current || filteredProducts.length === 0) return;

    const slides = slidesContainerRef.current.querySelectorAll(".swiper-slide");
    slides.forEach((slide, index) => {
      gsap.from(slide, {
        scale: 0.8,
        opacity: 0,
        duration: 0.8,
        delay: index * 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: slide,
          start: "top 90%",
          toggleActions: "play none none none",
        },
      });
    });
  }, [filteredProducts]);

  return (
    <section className="flex flex-col gap-8 py-12 px-4">
      {/* Section Title */}
      <h1
        ref={titleRef}
        className="text-center text-3xl sm:text-4xl md:text-5xl font-bold text-black"
      >
        {title}
      </h1>

      {/* Tabs */}
      <div className="flex justify-center flex-wrap gap-4">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            title={tab.title}
            handleClick={() => handleTabClick(tab.title)}
            isActive={activeTab === tab.title}
          />
        ))}
      </div>

      {/* Swiper */}
      <div
        ref={slidesContainerRef}
        className="w-full max-w-[95vw] mx-auto relative overflow-visible"
      >
        {filteredProducts.length > 0 ? (
          <Swiper
            modules={[Navigation, Autoplay]}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            spaceBetween={10}
            slidesPerView={1}
            loop={filteredProducts.length > 1}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            breakpoints={{
              480: { slidesPerView: 1, spaceBetween: 5 },
              640: { slidesPerView: 2, spaceBetween: 10 },
              768: { slidesPerView: 2, spaceBetween: 15 },
              1024: { slidesPerView: 3, spaceBetween: 15 },
              1280: { slidesPerView: 4, spaceBetween: 20 },
            }}
            className="w-full"
          >
            {filteredProducts.map((product) => (
              <SwiperSlide key={product.id || product.title}>
                <ProductCard products={product} />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <p className="text-center text-gray-500 text-lg mt-10">
            Loading Products...
          </p>
        )}

        {/* Navigation Buttons */}
        {filteredProducts.length > 1 && (
          <>
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="hidden sm:flex absolute top-1/2 left-2 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center -translate-y-1/2 z-30 hover:scale-110 transition-all"
            >
              <FaAngleLeft className="text-black text-2xl" />
            </button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="hidden sm:flex absolute top-1/2 right-2 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center -translate-y-1/2 z-30 hover:scale-110 transition-all"
            >
              <FaAngleRight className="text-black text-2xl" />
            </button>
          </>
        )}
      </div>
    </section>
  );
};

export default Fragrances;